/**
 * RTK Query endpoints for the agent-runtime service (@cluster/agent-runtime).
 * Endpoints are injected into the shared `apiSlice`. Base path after the dev
 * proxy + nginx rewrite: `/api/agent-runtime/*` → `agent-runtime:3601` and the
 * rewrite maps the outer proxy name onto the backend path stem `/agent`,
 * so `/agent-runtime/conversations` reaches `POST /agent/conversations`.
 */
import { apiSlice } from './apiSlice';
import type {
  Conversation,
  ConversationControl,
  CreateConversationDto,
  AgentMessage,
  MessageVersion,
  Run,
  AgentQueueStats,
  AgentStreamPart,
  AgentMessageFileRef,
} from '@/types';

export interface ConversationListParams {
  factoryId?: string;
  status?: string;
  projectId?: string;
  page?: number;
  limit?: number;
}

export interface ConversationQueryResult {
  data: Conversation[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface ConversationResourceParams {
  include?: 'messages' | 'versions' | 'runs' | string;
}

export interface ConversationWithResources {
  conversation: Conversation;
  messages?: AgentMessage[];
  versions?: MessageVersion[];
  runs?: Run[];
}

export interface StreamReplayResult {
  conversationId: string;
  status: string;
  limit: number;
  parts: Array<{ t: 'part'; part: AgentStreamPart; at: string }>;
}

export interface AddMessageDto {
  content?: string;
  fileRefs?: AgentMessageFileRef[];
  audioRefs?: string[];
  imageRefs?: string[];
}

export interface ForkConversationDto {
  messagePosition: number;
  modelConfigId?: string;
  name?: string;
}

export interface DelegateTarget {
  factoryId: string;
  payload?: Record<string, unknown>;
}

export interface DelegateConversationDto {
  targets: DelegateTarget[];
  mode?: 'bulk';
}

/**
 * Response of the runtime lifecycle controls (stop/pause/resume). The backend
 * sets `conversation.control` and returns the applied control state — the
 * status itself is flipped by the worker, so callers read `control`.
 */
export interface ControlResult {
  conversationId: string;
  control: ConversationControl;
  hard?: boolean;
  reenqueued?: boolean;
}

function data<T>(response: { data: T }): T {
  return response.data;
}

export const agentRuntimeApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── Conversations ─────────────────────────────────────────────────────
    getConversations: builder.query<ConversationQueryResult, ConversationListParams | void>({
      query: (params) => ({ url: '/agent-runtime/conversations', params: params || undefined }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id, _id }) => ({
                type: 'AgentConversation' as const,
                id: id || _id,
              })),
              { type: 'AgentConversation', id: 'LIST' },
            ]
          : [{ type: 'AgentConversation', id: 'LIST' }],
    }),

    getConversation: builder.query<
      ConversationWithResources,
      { id: string; include?: ConversationResourceParams['include'] }
    >({
      query: ({ id, include }) => ({
        url: `/agent-runtime/conversations/${id}`,
        params: include ? { include } : undefined,
      }),
      transformResponse: (response: Conversation | ConversationWithResources) => {
        // With `include` the backend returns { conversation, messages?, versions?, runs? };
        // without it the conversation doc itself is returned. Normalize to the
        // include shape so pages always read `.conversation`.
        const candidate = response as unknown as ConversationWithResources;
        if (candidate && 'conversation' in candidate) {
          return candidate;
        }
        return { conversation: response as unknown as Conversation };
      },
      providesTags: (_result, _error, { id }) => [{ type: 'AgentConversation', id }],
    }),

    createConversation: builder.mutation<
      { data: Conversation; conversationId: string },
      CreateConversationDto
    >({
      query: (body) => ({ url: '/agent-runtime/conversations', method: 'POST', body }),
      invalidatesTags: [{ type: 'AgentConversation', id: 'LIST' }],
    }),

    /** GET /agent-runtime/conversations/:id/stream-replay?limit=N (K=10 default). */
    getConversationStreamReplay: builder.query<StreamReplayResult, { id: string; limit?: number }>({
      query: ({ id, limit }) => ({
        url: `/agent-runtime/conversations/${id}/stream-replay`,
        params: limit ? { limit } : undefined,
      }),
      providesTags: (_result, _error, { id }) => [
        { type: 'AgentConversation', id: `stream-${id}` },
      ],
    }),

    // ── Messaging + controls ──────────────────────────────────────────────
    addConversationMessage: builder.mutation<
      { messageId: string; seq: number; queueAdded: boolean },
      { id: string; body: AddMessageDto }
    >({
      query: ({ id, body }) => ({
        url: `/agent-runtime/conversations/${id}/messages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'AgentConversation', id },
        { type: 'AgentConversation', id: 'LIST' },
        { type: 'AgentMessage', id: id },
      ],
    }),

    stopConversation: builder.mutation<
      ControlResult,
      { id: string; hard?: boolean }
    >({
      query: ({ id, hard }) => ({
        url: `/agent-runtime/conversations/${id}/stop`,
        method: 'POST',
        body: hard ? { hard } : {},
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'AgentConversation', id }],
    }),

    pauseConversation: builder.mutation<ControlResult, string>({
      query: (id) => ({ url: `/agent-runtime/conversations/${id}/pause`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'AgentConversation', id }],
    }),

    resumeConversation: builder.mutation<ControlResult, string>({
      query: (id) => ({ url: `/agent-runtime/conversations/${id}/resume`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'AgentConversation', id }],
    }),

    compactConversation: builder.mutation<{ status: string }, { id: string; prompt?: string }>({
      query: ({ id, prompt }) => ({
        url: `/agent-runtime/conversations/${id}/compact`,
        method: 'POST',
        body: prompt ? { prompt } : {},
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'AgentConversation', id }],
    }),

    forkConversation: builder.mutation<
      { conversationId: string; subConversationIds?: string[] },
      { id: string; body: ForkConversationDto }
    >({
      query: ({ id, body }) => ({
        url: `/agent-runtime/conversations/${id}/fork`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'AgentConversation', id: 'LIST' }],
    }),

    delegateConversation: builder.mutation<
      { conversationId: string; subConversationIds?: string[] },
      { id: string; body: DelegateConversationDto }
    >({
      query: ({ id, body }) => ({
        url: `/agent-runtime/conversations/${id}/delegate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'AgentConversation', id: 'LIST' }],
    }),

    setConversationModel: builder.mutation<
      { status: string },
      { id: string; modelConfigId: string }
    >({
      query: ({ id, modelConfigId }) => ({
        url: `/agent-runtime/conversations/${id}/model`,
        method: 'PATCH',
        body: { modelConfigId },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'AgentConversation', id }],
    }),

    completeConversation: builder.mutation<{ status: string }, string>({
      query: (id) => ({ url: `/agent-runtime/conversations/${id}/complete`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'AgentConversation', id },
        { type: 'AgentConversation', id: 'LIST' },
      ],
    }),

    // ── Queues observability (agent-worker vs agent-runtime stats) ────────
    getAgentQueueStats: builder.query<AgentQueueStats, void>({
      query: () => '/agent-runtime/queues/stats',
      providesTags: [{ type: 'AgentQueue', id: 'LIST' }],
    }),
  }),
});

// Export auto-generated hooks (endpoints injected onto the shared apiSlice).
export const {
  useGetConversationsQuery,
  useGetConversationQuery,
  useCreateConversationMutation,
  useGetConversationStreamReplayQuery,
  useLazyGetConversationStreamReplayQuery,
  useAddConversationMessageMutation,
  useStopConversationMutation,
  usePauseConversationMutation,
  useResumeConversationMutation,
  useCompactConversationMutation,
  useForkConversationMutation,
  useDelegateConversationMutation,
  useSetConversationModelMutation,
  useCompleteConversationMutation,
  useGetAgentQueueStatsQuery,
} = agentRuntimeApi;